// ============================================================================
// Main Chart Components
// ============================================================================
export { default as ShapeManager } from './components/Shapes/ShapeManager';
export { default as ShapeRender } from './components/Shapes/ShapeRender';
export { default as ShapePropertyManager } from './components/ShapeProperty/ShapePropertyManager';

// ============================================================================
// Chart Elements
// ============================================================================
export { default as ChartResizer } from './components/ChartElements/ChartResizer';
export { YCoordinatePopup } from './components/ChartElements/YCoordinatePopup';
export { PlaceOrderPopup } from './components/ChartElements/PlaceOrderPopup';
export { Dialog } from './components/ChartElements/dialog';
export { ChartSection } from './components/ChartElements/ChartSection';
export { PopupManager } from './components/ChartElements/PopupManager';
export { RatioManager } from './components/ChartElements/RatioManager';

// ============================================================================
// Chart Indicators
// ============================================================================
export { XAxis } from './components/ChartIndicators/XAxis';
export { YAxis } from './components/ChartIndicators/YAxis';
export { Axis } from './components/ChartIndicators/Axis';
export { AxisZoomCapture } from './components/ChartIndicators/AxisZoomCapture';

// ============================================================================
// View Components
// ============================================================================
export { ShapeListView } from './components/ShapeListView';
export { Toolbar } from './components/Toolbar';

// ============================================================================
// Interactive Shapes
// ============================================================================

export { TrendLine } from './components/Shapes/InteractiveShapes/TrendLine';
export { InteractiveText } from './components/Shapes/InteractiveShapes/InteractiveText';
export { FibonacciRetracement } from './components/Shapes/InteractiveShapes/FibonacciRetracement';
export { GannFan } from './components/Shapes/InteractiveShapes/GannFan';
export { FibChannel } from './components/Shapes/InteractiveShapes/FibChannel';
export { ElliottImpulse } from './components/Shapes/InteractiveShapes/ElliottImpulse';
export { Measure } from './components/Shapes/InteractiveShapes/Measure';
export { Rectangle } from './components/Shapes/InteractiveShapes/Rectangle';
export { Triangle } from './components/Shapes/InteractiveShapes/Triangle';
export { InteractiveOrderLine } from './components/Shapes/InteractiveShapes/InteractiveOrderLine';
export { _VerticalLine as VerticalLine } from './components/Shapes/InteractiveShapes/_VerticalLine';
export { MouseCoordinateY } from './components/Shapes/InteractiveShapes/MouseCoordinateY';

// ============================================================================
// Shape Configuration
// ============================================================================
export { convertToAppearance } from './utils/shapePropsToAppearance';
export { RatioForm } from './components/ChartElements/RatioDropDown';
export * from './components/ShapeProperty/shape.map';

// ============================================================================
// Mini Toolbar Components
// ============================================================================
export { default as ShapeMiniToolbar } from './components/MiniToolbar/ShapeMiniToolbar';
export { default as EditButton } from './components/MiniToolbar/EditButton';
export { default as DeleteShapeButton } from './components/MiniToolbar/DeleteShapeButton';

// ============================================================================
// Shape Property Components
// ============================================================================
export { default as DynamicRenderer } from './components/ShapeProperty/DynamicRenderer';

// Property Types
export { default as ExtendOption } from './components/ShapeProperty/PropertyTypes/ExtendOption';
export { default as CoordinateComponent } from './components/ShapeProperty/PropertyTypes/Coordinate';
export { default as ArrowOption } from './components/ShapeProperty/PropertyTypes/ArrowOption';
export { default as LineOptions } from './components/ShapeProperty/PropertyTypes/LineOptions';
export { default as FontOptionComponent } from './components/ShapeProperty/PropertyTypes/FontOption';
export { default as LineWidthDashPicker } from './components/ShapeProperty/PropertyTypes/LineWidthDashPicker';
export { default as ShowValueColorOptionComponent } from './components/ShapeProperty/PropertyTypes/ShowValueColorOption';
export { default as ShowOpacityOptionComponent } from './components/ShapeProperty/PropertyTypes/ShowOpacityOption';
export { default as ShowColorOptionComponent } from './components/ShapeProperty/PropertyTypes/ShowColorOption';
export { default as ShowLineOptionComponent } from './components/ShapeProperty/PropertyTypes/showLineOption';

// Property Widgets
export { default as ColorPicker } from './components/ShapeProperty/Widgets/ColorPicker';
export { default as Checkbox } from './components/ShapeProperty/Widgets/Checkbox';
export { default as FontSize } from './components/ShapeProperty/Widgets/FontSize';
export { default as FontFamily } from './components/ShapeProperty/Widgets/FontFamily';
export { default as DropDown } from './components/ShapeProperty/Widgets/DropDown';
export { default as InputBox } from './components/ShapeProperty/Widgets/InputBox';
export { default as FontStyle } from './components/ShapeProperty/Widgets/FontStyle';
export { default as Opacity } from './components/ShapeProperty/Widgets/LineOpacity';
export { default as LineWidth } from './components/ShapeProperty/Widgets/LineWidth';
export { default as AntText } from './components/ShapeProperty/Widgets/Text';
export { RadioGroup } from './components/ShapeProperty/Widgets/RadioGroup';

// ============================================================================
// Hooks
// ============================================================================
export { useGlobalShapeSelector, scanOrderLineConflict } from './contexts/GlobalShapesContext';
export type { ConflictResult } from './contexts/GlobalShapesContext';
export { useChartUIStoreSelector } from './stores/chartUIStore';
export { globalShapeStore } from './stores/globalShapeStore';
export type { ChartUIStoreInstance } from './stores/chartUIStore';
export { useShapeInteraction } from './hooks/use-shape-interaction';

export { ShapeType, ShapePropertyInputType } from './types/commontypes';

export type {
  ActivePopup,
  ChartId,
  DynamicComponentProps,
  ShapePropertyInputDef,
  RenderNodesMap,
  ShapeSelection,
  RenderNode,
  Levels,
  ShapeAppearanceUpdater,
  ChartInfo as ChartHeight,
  ChartInfo,
  RatioDropdown,
  MoreProps,
  DrawingStatus,
  DrawingSharedState,
  ElliottImpulseSharedState,
  MeasureSharedState,
  RFCInteractiveItem,
  RenderNodeMap,
} from './types/commontypes';

export type {
  Coordinate,
  LineWidthDashOption,
  LineOption,
  FontOption,
  ShowColorOption,
  ShowValueColorOption,
  ShowOpacityOption,
  Text,
  FontBasicOption,
  ShowLineOption,
} from './types/properties';

// ============================================================================
// Utils
// ============================================================================
export * from './utils/shapeHelpers';
export { deleteShape, useDeleteShape } from './utils/deleteShape';
export * from './utils/useShapeActions';
export * from './utils/utils';
export { isClickOnShape } from './utils/getClickPositionOnChart';

// ============================================================================
// Context
// ============================================================================
export * from './contexts';

// ============================================================================
// Global Options
// ============================================================================
export { setGlobalOptions, globalOptions } from './config/globalOptions';
export type { GlobalOptionsType } from './config/globalOptions';

// ============================================================================
// CSS and SVG
// ============================================================================
import styles from './styles/global.module.css';
export default styles;

export * from './components/DynamicSvg';

export type { ResizeBarConfig } from './components/ChartElements/ChartResizer';

export { ModalDragger } from './components/ShapeProperty/modal-dragger';
